export interface Command {
  do(): void;
  undo(): void;
}

export type HistoryChangeKind = "execute" | "undo" | "redo" | "discard" | "clear";

/**
 * `"clear"` ne porte pas de commande : il signale que les deux piles ont été vidées d'un coup
 * (voir `clear`), pas qu'une commande précise a bougé. L'union (plutôt qu'un `command` optionnel)
 * garde `event.command` non-nul pour les 4 autres types, sans garde à écrire chez l'appelant.
 */
export type HistoryChangeEvent =
  | { kind: Exclude<HistoryChangeKind, "clear">; command: Command }
  | { kind: "clear" };

export type HistoryListener = (event: HistoryChangeEvent) => void;

/**
 * Commandes exécutées entre `History.beginGroup()` et `endGroup()` : une seule entrée, défaite et
 * refaite d'un bloc.
 */
export class CommandGroup implements Command {
  constructor(readonly commands: Command[]) {}

  do(): void {
    for (const command of this.commands) command.do();
  }

  undo(): void {
    for (let i = this.commands.length - 1; i >= 0; i--) this.commands[i]!.undo();
  }
}

/** Pile undo/redo générique (BESOINS.md §6), agnostique du type de commande. */
export class History {
  private undoStack: Command[] = [];
  private redoStack: Command[] = [];
  private listeners = new Set<HistoryListener>();
  private pendingGroup: Command[] | null = null;
  private groupDepth = 0;

  /**
   * Ouvre un groupe : les commandes exécutées jusqu'au `endGroup()` correspondant ne formeront
   * qu'une entrée. Les groupes s'imbriquent (seul le plus extérieur compte).
   */
  beginGroup(): void {
    if (this.groupDepth++ === 0) this.pendingGroup = [];
  }

  endGroup(): void {
    if (this.groupDepth === 0 || --this.groupDepth > 0) return;
    const commands = this.pendingGroup ?? [];
    this.pendingGroup = null;
    if (commands.length === 0) return;
    const entry = commands.length === 1 ? commands[0]! : new CommandGroup(commands);
    this.undoStack.push(entry);
    this.redoStack = [];
  }

  execute(command: Command): void {
    if (this.pendingGroup) {
      command.do();
      this.pendingGroup.push(command);
      this.redoStack = [];
      this.notify({ command, kind: "execute" });
      return;
    }
    command.do();
    this.undoStack.push(command);
    this.redoStack = [];
    this.notify({ command, kind: "execute" });
  }

  /**
   * Un groupe notifie chacune de ses commandes, pas le groupe lui-même : les écouteurs (ex.
   * `ShapeSaveTracker.runHistoryChange`) ne connaissent que les commandes qu'ils ont exécutées.
   */
  undo(): void {
    const command = this.undoStack.pop();
    if (!command) return;
    command.undo();
    this.redoStack.push(command);
    const notified = command instanceof CommandGroup ? [...command.commands].reverse() : [command];
    for (const inner of notified) this.notify({ command: inner, kind: "undo" });
  }

  redo(): void {
    const command = this.redoStack.pop();
    if (!command) return;
    command.do();
    this.undoStack.push(command);
    const notified = command instanceof CommandGroup ? command.commands : [command];
    for (const inner of notified) this.notify({ command: inner, kind: "redo" });
  }

  /**
   * Retire une commande précise de la pile où qu'elle se trouve (Annuler OU Rétablir) —
   * contrairement à `undo()`/`redo()`, pas nécessairement celle du sommet, et sans appeler
   * `command.do()`/`undo()`. Sert à annuler une modification dont la sauvegarde distante a échoué
   * (voir `TokaMapConfig.onSave`/`ShapeSaveTracker`) sans perturber des commandes plus récentes
   * exécutées entre-temps sur d'autres formes (qui, elles, restent dans leur pile). Cherche dans les
   * deux piles car `command` peut avoir été déplacée par un `undo()`/`redo()` manuel entre son
   * exécution et l'échec de sa sauvegarde (voir `ShapeSaveTracker.runHistoryChange`). Sans effet si
   * la commande n'est plus dans aucune des deux piles.
   */
  discard(command: Command): void {
    let index = this.undoStack.indexOf(command);
    if (index !== -1) {
      this.undoStack.splice(index, 1);
      this.notify({ command, kind: "discard" });
      return;
    }
    index = this.redoStack.indexOf(command);
    if (index === -1) return;
    this.redoStack.splice(index, 1);
    this.notify({ command, kind: "discard" });
  }

  /**
   * Vide les deux piles sans rien annuler : les commandes déjà appliquées le restent, elles
   * deviennent seulement impossibles à annuler. Sert au changement de document
   * (`TokaMapInstance.loadDocument`), où annuler une commande portant sur des formes qui ne sont
   * plus chargées n'aurait aucun sens.
   */
  clear(): void {
    if (!this.undoStack.length && !this.redoStack.length) return;
    this.undoStack = [];
    this.redoStack = [];
    this.notify({ kind: "clear" });
  }

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  onChange(listener: HistoryListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(event: HistoryChangeEvent): void {
    for (const listener of this.listeners) listener(event);
  }
}
